import styled from '@emotion/styled';
import {media, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {Button} from 'UI/components/action/Button';
import {InlineLink} from 'UI/components/action/InlineLink';
import {PayloadImage} from 'UI/components/layout/PayloadImage';
import {PayloadLexicalSmall} from 'UI/components/layout/PayloadLexicalSmall';
import {H6, P} from 'UI/components/layout/Text';
import {useSwmCard} from 'UI/components/modules/Steps/SwmCard/useSwmCard';

import {FadeInAnimation} from 'UI/animations/shared';

import {LogoSwmInv} from 'Images/icons';

import type {LexicalContent} from '@nfq/payload-lexical-parser';
import type {LinkField, Media} from 'types/payload-types';

/**
 * The props for the `SwmCard` component, which renders hotline details,
 * a CTA button, an image, and optional descriptive text.
 * This interface defines the structure of the content required to render
 * the card, including hotline configuration, a button, and test identifiers
 * for automation.
 */
interface ComponentProps {
    /**
     * The configuration for the call-to-action (CTA) button.
     * Defines the label, type, and target for the button link.
     */
    button?: LinkField;
    /**
     * Optional headline text displayed at the top of the card.
     * Used to introduce or label the content of the `SwmCard`.
     */
    headline?: string | null;
    /**
     * The hotline link configuration, defining the phone number or other
     * clickable action for contacting support.
     */
    hotlineLink?: LinkField;
    /**
     * Optional supporting text displayed next to the hotline link.
     * Often used to provide context, such as "Rufen Sie uns an:".
     */
    hotlineText?: string | null;
    /**
     * The optional image displayed at the top of the `SwmCard`.
     * Typically used for an icon, illustration, or supporting graphic.
     */
    image?: Media | string | null;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'SwmCard'
     */
    testId?: string;
    /**
     * Optional descriptive text content for the `SwmCard`, rendered as
     * rich text using the `PayloadLexical` component.
     */
    text?: LexicalContent | null;
}

/**
 * A card component that highlights hotline information, an optional service description,
 * and a call-to-action button.
 * The `SwmCard` displays an image, headline, hotline text with a linked phone number,
 * and a configurable CTA button. The `useSwmCard` hook is used to normalize both hotline
 * and button link data (`href`, `label`, `rel`, `target`), ensuring consistent behavior
 * for internal, external, or file links.
 * The content section is split into two parts: the hotline information and CTA button at
 * the top, and the descriptive text below the divider. The descriptive text is rendered
 * using `PayloadLexical` for rich text formatting.
 * The component is wrapped in MobX’s `observer` so it reacts to observable data changes.
 * The `testId` prop is applied as `data-cy` for reliable test automation.
 *
 * @param props             The component props.
 * @param props.button      The CTA button configuration, including label and link details.
 * @param props.headline    Optional headline displayed at the top of the card.
 * @param props.hotlineLink The hotline link configuration, defining the phone number or URL.
 * @param props.hotlineText Optional supporting text displayed alongside the hotline link.
 * @param props.image       The image displayed at the top of the card.
 * @param props.text        Optional descriptive text content rendered below the divider.
 * @param props.testId      An optional identifier used for testing, applied as `data-cy`. Defaults to `'SwmCard'`.
 * @returns A JSX element rendering the hotline card with an image, headline, hotline link, CTA button, and optional description.
 *
 * @example
 * ```tsx
 * <SwmCard
 *   headline="24/7 Hotline"
 *   hotlineText="Rufen Sie uns an:"
 *   hotlineLink={{ label: "0800 123 456", linkType: "phone", url: "tel:0800123456" }}
 *   button={{ label: "Kostenlos beauftragen", linkType: "internal", internal: "/order" }}
 *   image={hotlineImage}
 *   text="Unser Kundenservice ist jederzeit für Sie da."
 * />
 * ```
 */
const SwmCard = observer(({
    button,
    headline,
    hotlineLink,
    hotlineText,
    image,
    testId = 'SwmCard',
    text
}: ComponentProps) => {
    const {buttonHref, buttonLabel, href, label, rel, target} = useSwmCard(hotlineLink, button);

    return (
        <Wrapper data-cy={testId} variants={FadeInAnimation}>
            <ImageWrapper variants={FadeInAnimation}>
                <Image
                    expectedSizes={{
                        xs: 522, sm: 708, md: 350, lg: 420, xl: 420, xxl: 420
                    }}
                    image={image}
                    isPresentational
                />
                <SwmOverlay>
                    <LogoSwmInv height={16} width={48} />
                </SwmOverlay>
            </ImageWrapper>
            <TextWrapper>
                <FirstPart>
                    <HotlineWrapper>
                        {headline && <MotionH6 as="h4" variants={FadeInAnimation}>{headline}</MotionH6>}
                        {hotlineText && (
                            <MotionP variants={FadeInAnimation}>
                                {hotlineText} <InlineLink href={href!}>{label}</InlineLink>
                            </MotionP>
                        )}
                    </HotlineWrapper>
                    <MotionButton
                        as="link"
                        href={buttonHref}
                        rel={rel}
                        target={target}
                        variants={FadeInAnimation}
                    >
                        {buttonLabel}
                    </MotionButton>
                </FirstPart>
                <Divider />
                {text && (
                    <motion.div variants={FadeInAnimation}>
                        <PayloadLexicalSmall description={text} />
                    </motion.div>
                )}
            </TextWrapper>
        </Wrapper>
    );
});

SwmCard.displayName = 'SwmCard';

export {SwmCard};

const Wrapper = styled(motion.div)`
    border: 1px solid ${({theme}) => theme.colors.neutral200};
    border-radius: 24px;
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
    width: 100%;

    ${media('lg')} {
        flex-direction: row;
    }
`;

const ImageWrapper = styled(motion.div)`
    border-radius: 24px 24px 0 0;
    height: 20rem;
    position: relative;
    width: 100%;

    ${media('lg')} {
        border-radius: 24px 0 0 24px;
        height: 100%;
        width: 42rem;
    }
`;

const Image = styled(PayloadImage)`
    border-radius: 24px 24px 0 0;
    height: 20rem;
    object-fit: cover;
    width: 100%;

    ${media('lg')} {
        border-radius: 24px 0 0 24px;
        height: 100%;
        width: 42rem;
    }
`;

const SwmOverlay = styled.div`
    align-items: center;
    backdrop-filter: blur(9.538597106933594px);
    background: rgba(244, 244, 245, 0.20);
    border: 1px solid rgba(255, 255, 255, 0.40);
    border-radius: 24px;
    display: flex;
    height: 72px;
    justify-content: center;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 72px;
`;

const TextWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(6)};
    padding: ${spacing(12)} ${spacing(12)} ${spacing(12)} ${spacing(8)};
    width: 100%;
`;

const FirstPart = styled.div`
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    row-gap: ${spacing(6)};
`;

const HotlineWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(6)};
`;

const MotionH6 = styled(motion.create(H6))``;

const MotionP = styled(motion.create(P))``;

const MotionButton = styled(motion.create(Button))``;

const Divider = styled.div`
    border-bottom: 1px solid ${({theme}) => theme.colors.neutral200};
    height: 0px;
    width: 100%;
`;